import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import NotificationDropdown from "./Notification";
import ProfileDropdown from "./ProfileDropdown";
import { getUserDetails } from "apis/homeServices";
import { getUserProfile } from "apis/settingsServices";
import { useUserStore } from "store/userStore";
import { extractAirlineCode } from "utils/stringUtils";
import HeadrtBackGround from "assets/images/top-header-1.png";
import NotificationBell from "assets/images/NotificationBell.svg";
import LoadsIcon from "assets/images/search-Icon.svg";
import YourRq from "assets/images/YourReqIcon.svg";
import Request from "assets/images/RequestIcon.svg";
import Settings from "assets/images/Settings.svg";
import Credits from "assets/images/CreditIcon.svg";
import MainLogo from "assets/images/MainLogo.png";
import burger from "assets/images/burger-simple.svg";
import ReferralModal from "./ReferralCard";

const Header: React.FC = () => {
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const navigate = useNavigate();
  const userRole = useUserStore((state) => state.role);
  const baseProfileItems = [
    {
      label: "Invite a Friend",
      icon: "st-invite",
      onClick: () => setIsReferralModalOpen(true),
    },
  ];
  const profileItems =
    userRole === "user"
      ? [
          ...baseProfileItems,
          {
            label: "Contact Us",
            icon: "st-chat",
            onClick: () =>
              navigate("/settings?section=contactUs", {
                state: { section: "contactUs" },
              }),
          },
        ]
      : baseProfileItems;

  const primaryAirlineCode = useUserStore((state) => state.primaryAirlineCode);

  const userRoutes = [
    { to: "/search", icon: LoadsIcon, label: "Search" },
    { to: "/my-loads", icon: YourRq, label: "Enquiries" },
    { to: "/my-requests", icon: Request, label: primaryAirlineCode ? `Request - ${primaryAirlineCode}` : "Request" },
    { to: "/settings", icon: Settings, label: "Settings" },
    { to: "/credits", icon: Credits, label: "Token" },
  ];

  const adminRoutes = [
    { to: "/admin/dashboard", icon: "st-dashboard", label: "Dashboard" },
    { to: "/admin/user-management", icon: "st-user", label: "User Management" },
    {
      to: "/admin/credit-management",
      icon: "st-credits",
      label: "Token Management",
    },
    {
      to: "/admin/report&feedback",
      icon: "st-comment",
      label: "Report & Feedback",
    },
    {
      to: "/admin/discount-management",
      icon: "st-discount",
      label: "Discount Management",
    },
    {
      to: "/admin/non-rev-config",
      icon: "st-settings", // Using settings icon for now as no specific icon was provided
      label: "Non-Rev Config",
    },
    { to: "/admin/settings", icon: "st-settings", label: "Settings" },
  ];

  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const [userDetails, setUserDetails] = useState<any>(() => {
    const savedUserDetails = localStorage.getItem("userDetails");
    return savedUserDetails ? JSON.parse(savedUserDetails) : null;
  });

  const [openRequestCount] = useState<number>(0);

  useEffect(() => {
    const fetchUserDetails = async () => {
      const savedUserDetails = localStorage.getItem("userDetails");

      if (!savedUserDetails && !userDetails) {
        try {
          const response: any = await getUserDetails();
          setUserDetails(response);
          console.log(response);
          useUserStore.getState().setUsername(response.userName);
          useUserStore.getState().setEmail(response.email);
          useUserStore.getState().setUserRole(response.role);
          useUserStore.getState().setReferralCode(response.referralCode);
        } catch (error) {
          console.error("Failed to fetch user details:", error);
        }
      }
    };

    if (!userDetails) {
      fetchUserDetails();
    }
  }, [userDetails]);

  // Fetch user profile to get primary airline code
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const profileData = await getUserProfile();
        if (profileData?.primaryAirline?.airlineName) {
          const airlineCode = extractAirlineCode(profileData.primaryAirline.airlineName);
          if (airlineCode) {
            useUserStore.getState().setPrimaryAirlineCode(airlineCode);
          }
        }
      } catch (error) {
        console.error("Failed to fetch user profile for airline code:", error);
      }
    };

    // Only fetch if we don't already have the airline code
    if (!primaryAirlineCode && userRole === "user") {
      fetchUserProfile();
    }
  }, [primaryAirlineCode, userRole]);

  // // 🔔 Fetch open request count for badge
  // useEffect(() => {
  //   const fetchOpenRequestCount = async () => {
  //     try {
  //       const response = await getFlightRequests({
  //         pageNumber: 1,
  //         pageSize: 5,
  //         status: "open",
  //       });
  //       setOpenRequestCount(response.totalCount);
  //     } catch (error) {
  //       console.error("Failed to fetch open request count:", error);
  //     }
  //   };

  //   fetchOpenRequestCount();
  // }, []);

  // Modify "Requests" label to include badge
  const modifiedUserRoutes = userRoutes.map((route) => {
    if (route.to === "/request") {
      return {
        ...route,
        label: (
          <div className="flex items-center gap-1">
            <span>My Requests</span>
            {openRequestCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {openRequestCount}
              </span>
            )}
          </div>
        ),
      };
    }
    return route;
  });

  // NavLink helpers
  const getNavClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center py-5 gap-2 relative ${isActive ? "rounded-md max:" : ""}`;

  const getIconClass = (isActive: boolean) => isActive ? "text-[#E29100] text-sm mr-1" : "text-[#FFFFFFCC] text-sm mr-1";
                                              
  const getTextClass = (isActive: boolean) =>
    isActive ? "text-[#E29100] text-[16px]" : "text-[#FFFFFFCC] text-[16px]";

  return (
    <header
      style={{ backgroundImage: `url(${HeadrtBackGround})` }}
      className="bg-cover"
    >
      <nav className="fbg-white px-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Link
            to="/search"
            className="flex items-center space-x-3 rtl:space-x-reverse"
          >
            <img className="w-44 my-4" src={MainLogo} alt="Logo" />
          </Link>

          <button
            onClick={() => setIsNavbarOpen(!isNavbarOpen)}
            type="button"
            className="inline-flex items-center ms-auto p-2 w-10 h-10 justify-center text-sm text-gray-500 rounded-lg md:hidden"
          >
            <img src={burger} alt="Menu" className="w-12 h-12" />
            <span className="sr-only">Open main menu</span>
          </button>

          <div
            className={`${isNavbarOpen ? "block" : "hidden"} w-full md:block md:w-auto`}
            id="navbar-dropdown"
          >
            <ul className="flex md:flex-row flex-col gap-2 font-medium">
              {userRole === "user" &&
                modifiedUserRoutes.map(({ to, icon, label }) => (
                  <li key={to}>
                    <NavLink to={to} className={getNavClass}>
                      {({ isActive }) => (
                        <div
                          className={`relative flex flex-col items-center rounded-full px-4 py-2 ${isActive ? "bg-white" : ""}`}
                        >
                          {/* <i className={`${icon} ${getIconClass(isActive)}`}></i> */}
                          {/* <img className="w-6 h-6" src={icon} /> */}
                          <span className={getTextClass(isActive)}>
                            {label}
                          </span>
                          {/* {isActive && <img className="w-6 h-6 absolute -bottom-4 left-1/2 -translate-x-1/2 z-50 max-md:hidden " src={Polygon} />} */}
                        </div>
                      )}
                    </NavLink>
                  </li>
                ))}
              {userRole === "admin" &&
                adminRoutes.map(({ to, icon, label }) => (
                  <li key={to}>
                    <NavLink to={to} className={getNavClass}>
                      {({ isActive }) => (
                        <div className={`relative flex items-center rounded-full px-4 py-2 ${isActive ? "bg-white" : ""}`}>
                          <i
                            className={`${icon} ${getIconClass(isActive)}`}
                          ></i>
                          <span className={getTextClass(isActive)}>
                            {label}
                          </span>
                          {/* <img className="w-6 h-6" src={icon} /> */}
                        </div>
                      )}
                    </NavLink>
                  </li>
                ))}
            </ul>
          </div>

          <div className="flex items-center gap-6 md:mb-0 mb-2 ">
            <div id="dropdown-1 ">
              {userRole === "user" && <NotificationDropdown icon={NotificationBell} />}
            </div>
            <div id="dropdown" className="border border-[#FFFFFF4D] rounded-full">
              <ProfileDropdown
                userAvatar="/images/avatar.png"
                items={profileItems}
              />
            </div>
          </div>
        </div>
      </nav>
      <ReferralModal
        isOpen={isReferralModalOpen}
        onClose={() => setIsReferralModalOpen(false)}
      />
    </header>
  );
};

export default Header;
